class StickyNav {
  constructor() {
    this.navs = Array.prototype.slice.call(document.querySelectorAll('[data-sticky-nav]'));
    this.rafId = null;
    this.lockedUntil = 0;

    if (this.navs.length) {
      this.navs.forEach((nav) => this.init(nav));
    }
  }

  init(nav) {
    const links = Array.prototype.slice.call(nav.querySelectorAll('[data-sticky-nav-link]'));
    if (!links.length) {
      return;
    }

    const sections = [];

    links.forEach((link) => {
      const label = link.querySelector('.lc-sticky-nav__label');
      const text = label ? label.textContent.trim() : link.textContent.trim();
      const id = this.slugify(text);

      if (!id) {
        return;
      }

      link.setAttribute('href', `#${id}`);
      this.ensureAnchor(id, text);

      const target = document.getElementById(id);
      if (target) {
        sections.push({ link, target, id });
      }

      link.addEventListener('click', (event) => {
        event.preventDefault();
        // Keep click target active while smooth-scroll settles
        this.lockedUntil = Date.now() + 900;
        this.scrollToId(id);
        this.setActive(links, link);
        this.scrollActiveIntoView(link);
        if (history.replaceState) {
          history.replaceState(null, '', `#${id}`);
        }
      });
    });

    if (!sections.length) {
      return;
    }

    const onScroll = () => {
      if (this.rafId) {
        return;
      }
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.updateActiveFromScroll(links, sections);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // Initial state (hash / top of page)
    this.updateActiveFromScroll(links, sections);
  }

  /**
   * Waypoint-style spy: last section whose top has passed the sticky offset line wins.
   */
  updateActiveFromScroll(links, sections) {
    if (Date.now() < this.lockedUntil) {
      return;
    }

    const line = this.getOffset();
    let active = sections[0];

    for (let i = 0; i < sections.length; i += 1) {
      const top = sections[i].target.getBoundingClientRect().top;
      if (top - line <= 1) {
        active = sections[i];
      } else {
        break;
      }
    }

    if (active && active.link) {
      this.setActive(links, active.link);
    }
  }

  ensureAnchor(id, text) {
    if (document.getElementById(id)) {
      return;
    }

    // Match Livingdocs anchors that still contain the label text
    const anchors = Array.prototype.slice.call(document.querySelectorAll('.lc-anchor, a.lc-anchor, a.ns-anchor'));
    const match = anchors.find((anchor) => {
      const existingId = anchor.getAttribute('id');
      const content = (anchor.textContent || '').trim();
      return existingId === id || existingId === text || this.slugify(content) === id || content === text;
    });

    if (match) {
      match.setAttribute('id', id);
      return;
    }

    // Fallback: create a marker at the first heading matching the label
    const headings = Array.prototype.slice.call(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    const heading = headings.find((node) => this.slugify(node.textContent.trim()) === id || node.textContent.trim() === text);
    if (heading) {
      heading.setAttribute('id', id);
    }
  }

  setActive(links, activeLink) {
    const changed = activeLink && !activeLink.classList.contains('is-active');

    links.forEach((link) => {
      link.classList.toggle('is-active', link === activeLink);
    });

    if (changed) {
      this.scrollActiveIntoView(activeLink);
    }
  }

  /**
   * Mobile: keep the active tab near the left edge of the horizontal scroller.
   */
  scrollActiveIntoView(activeLink) {
    if (window.matchMedia('(min-width: 992px)').matches) {
      return;
    }

    const list = activeLink.closest('.lc-sticky-nav__list');
    if (!list || list.scrollWidth <= list.clientWidth + 1) {
      return;
    }

    const item = activeLink.closest('.lc-sticky-nav__item') || activeLink;
    const listRect = list.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    // Keep active item near the left with the same 25px edge inset as the first item
    const leftInset = 25;
    const delta = itemRect.left - listRect.left - leftInset;
    const nextLeft = Math.max(0, Math.min(list.scrollLeft + delta, list.scrollWidth - list.clientWidth));

    if (Math.abs(nextLeft - list.scrollLeft) < 2) {
      return;
    }

    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left: nextLeft, behavior: 'smooth' });
    } else {
      list.scrollLeft = nextLeft;
    }
  }

  scrollToId(id) {
    const target = document.getElementById(id);
    if (!target) {
      return;
    }

    const top = target.getBoundingClientRect().top + window.pageYOffset - this.getOffset();
    window.scrollTo({
      top,
      behavior: 'smooth',
    });
  }

  getOffset() {
    const header = document.querySelector('.wp-header');
    const stickyNav = document.querySelector('.lc-sticky-nav');
    let offset = 0;

    if (header) {
      offset += header.getBoundingClientRect().height || 0;
    }

    if (stickyNav) {
      offset += stickyNav.getBoundingClientRect().height || 0;
    }

    if (document.documentElement.classList.contains('admin-bar')) {
      offset += window.innerWidth >= 783 ? 32 : 46;
    }

    // Breathing room below sticky header + nav
    return offset + 80;
  }

  slugify(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

export default StickyNav;
